import { Routes } from '@angular/router';
import { authGuard, notAuthGuard, adminAuthGuard, tokenGuard } from './core/guards/auth.guard';

import { HomeComponent } from './features/main/home/home.component';

import { MainComponent } from './features/main/main.component';

import { AuthComponent } from './features/auth/auth.component';
import { LoginComponent } from './features/auth/login/login.component';
import { ForgotPasswordComponent } from './features/auth/forgot-password/forgot-password.component';
import { ChangePasswordComponent } from './features/auth/change-password/change-password.component';
import { SignUpComponent } from './features/auth/signup/signup.component';

import { AdminComponent } from './features/admin/admin.component';
import { AccountComponent } from './features/main/settings/account/account.component';
import { OrganizationComponent } from './features/admin/organization/organization.component';
import { OrganizationDetailComponent } from './features/admin/organization/organization-detail/organization-detail.component';
import { UsersComponent } from './features/admin/users/users.component';
import { OrganizationGeneralComponent } from './features/admin/organization/organization-general/organization-general.component';
import { AzureCallbackComponent } from './features/auth/azure-callback/azure-callback.component';
import { ProjectsComponent } from './features/main/project/components/projects/projects.component';
import { projectDetailGuard, projectGuard } from './features/main/project/project.guard';
import { ProjectCreateComponent } from './features/main/project/components/project-create/project-create.component';
import { ProjectUpdateComponent } from './features/main/project/components/project-update/project-update.component';
import {
    timesheetContributorGuard,
    timesheetEditingGuard,
    timesheetGuard,
    timesheetMissingWeekGuard,
} from './features/main/timesheet/timesheet.guard';
import { TimesheetEditComponent } from './features/main/timesheet/component/timesheet-edit/timesheet-edit.component';
import { ProjectReadComponent } from './features/main/project/components/project-read/project-read.component';

import { PhaseReadComponent } from './features/main/project/components/project-read/project-general/phase-read/phase-read.component';
import { ContributorComponent } from './features/main/timesheet/component/contributor/contributor.component';
import { DashboardBillingComponent } from './features/main/dashboard/components/dashboard-billing/dashboard-billing.component';
import {
    dashboardBillingGuard,
    dashboardTimesheetGuard,
} from './features/main/dashboard/dashboard.guard';
import { DashboardTimesheetComponent } from './features/main/dashboard/components/dashboard-timesheet/dashboard-timesheet.component';
import { ProjectGeneralComponent } from './features/main/project/components/project-read/project-general/project-general.component';
import { ProjectTimesheetsComponent } from './features/main/project/components/project-read/project-timesheets.component/project-timesheets.component';
import { ProjectBillingComponent } from './features/main/project/components/project-read/project-billing/project-billing.component';
import { ProjectSubcontractingComponent } from './features/main/project/components/project-read/project-subcontracting/subcontracting.component';

export const appRoutes: Routes = [
    {
        path: '',
        component: AuthComponent,
        canActivate: [notAuthGuard],
        children: [
            {
                path: '',
                component: LoginComponent,
            },
        ],
    },
    {
        path: 'azure-callback',
        component: AzureCallbackComponent, // callback MSAL
        canActivate: [],
    },

    {
        path: 'forgot-password',
        component: AuthComponent,
        children: [
            {
                path: '',
                component: ForgotPasswordComponent,
            },
        ],
    },
    {
        path: 'change-password',
        component: AuthComponent,
        canActivate: [tokenGuard],
        children: [
            {
                path: '',
                component: ChangePasswordComponent,
            },
        ],
    },
    {
        path: 'signup',
        component: AuthComponent,
        canActivate: [tokenGuard],
        children: [
            {
                path: '',
                component: SignUpComponent,
            },
        ],
    },
    {
        path: 'main',
        component: MainComponent,
        canActivate: [authGuard, projectGuard],
        children: [
            {
                path: '',
                component: HomeComponent,
                canActivate: [timesheetMissingWeekGuard, dashboardBillingGuard],
            },
            {
                path: 'dashboard',
                children: [
                    {
                        path: 'billing',
                        component: DashboardBillingComponent,
                        canActivate: [dashboardBillingGuard],
                    },
                    {
                        path: 'timesheet',
                        component: DashboardTimesheetComponent,
                        canActivate: [dashboardTimesheetGuard],
                    },
                ],
            },
            {
                path: 'timesheet',
                canActivate: [],
                children: [
                    {
                        path: '',
                        component: TimesheetEditComponent,
                        canActivate: [timesheetGuard],
                        canDeactivate: [timesheetEditingGuard],
                    },
                    {
                        path: 'sheet',
                        children: [
                            {
                                path: '',
                                component: TimesheetEditComponent,
                                canActivate: [timesheetGuard],
                                canDeactivate: [timesheetEditingGuard],
                            },
                            {
                                path: ':from',
                                component: TimesheetEditComponent,
                                canActivate: [timesheetGuard],
                                canDeactivate: [timesheetEditingGuard],
                            },
                        ],
                    },

                    {
                        path: 'contributor',
                        children: [
                            {
                                path: '',
                                component: ContributorComponent,
                                canActivate: [timesheetContributorGuard],
                            },
                            {
                                path: ':userid',
                                component: ContributorComponent,
                                canActivate: [timesheetContributorGuard],
                            },
                        ],
                    },
                ],
            },
            {
                path: 'projects',
                canActivate: [],
                children: [
                    {
                        path: '',
                        component: ProjectsComponent,
                    },
                    {
                        path: 'all',
                        component: ProjectsComponent,
                    },
                    { path: 'new', component: ProjectCreateComponent },
                    {
                        path: ':code',
                        canActivate: [projectDetailGuard],
                        children: [
                            {
                                path: '',
                                component: ProjectReadComponent,
                                children: [
                                    {
                                        path: 'general',
                                        component: ProjectGeneralComponent,
                                    },
                                    {
                                        path: 'billing',
                                        component: ProjectBillingComponent,
                                    },
                                    {
                                        path: 'subcontracting',
                                        component: ProjectSubcontractingComponent,
                                    },
                                    {
                                        path: 'timesheets',
                                        component: ProjectTimesheetsComponent,
                                    },
                                ],
                            },
                            { path: 'edit', component: ProjectUpdateComponent },
                            {
                                path: 'phase',
                                children: [
                                    {
                                        path: ':phasecode',
                                        component: PhaseReadComponent,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            { path: 'account', component: AccountComponent },
        ],
    },
    {
        path: 'admin',
        component: AdminComponent,
        canActivate: [adminAuthGuard],
        children: [
            { path: 'organization', component: OrganizationComponent },
            {
                path: 'organization-detail/:id',
                component: OrganizationDetailComponent,
                children: [
                    { path: 'general', component: OrganizationGeneralComponent },
                    { path: 'users', component: UsersComponent },
                ],
            },
        ],
    },
];
